import React, { FC, lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { PageLayout } from './PageLayout';
import { LightHelmet } from '../../components/light-helmet/LightHelmet';
import { ACCOUNT_NAV_ITEMS } from '../../../../shared/constants/account/accountNavConstants';
import { UPDATE_EMAIL_URL } from '../../../../shared/constants/urlConstants';
import { useUser } from '../../user/useUser';
import { canSeeDocumentAnalyzer } from '../../../../shared/paying-options/canAccessDocumentAnalyzer';
import { canSeeDocumentsComparison } from '../../../../shared/paying-options/canAccessDocumentsComparison';
import { PageLayoutFallback } from './PageLayoutFallback';

const SearchMain = lazy(
  () =>
    import(
      /* webpackChunkName: "search" */ '../../search/components/search-layout/SearchMain'
    ),
);

const Watchers = lazy(
  () => import(/* webpackChunkName: "watchers" */ '../../watchers/Watchers'),
);

const WatchersDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "watchers" */ '../../watchers/details/WatchersDetails'
    ),
);

const DocumentAnalyzerApp = lazy(
  () =>
    import(
      /* webpackChunkName: "documentAnalyzer" */ '../../document-analyzer/router/DocumentAnalyzerApp'
    ),
);

const DocumentAnalyzerAnalysisApp = lazy(
  () =>
    import(
      /* webpackChunkName: "documentAnalyzerAnalysis" */ '../../document-analyzer/router/DocumentAnalyzerAnalysisApp'
    ),
);

const DocumentAnalyzerComparisonApp = lazy(
  () =>
    import(
      /* webpackChunkName: "documentAnalyzerComparison" */ '../../document-analyzer/router/DocumentAnalyzerComparisonApp'
    ),
);

const HistoryApp = lazy(
  () =>
    import(
      /* webpackChunkName: "history" */ '../../history/components/HistoryApp'
    ),
);

const FoldersRoutes = lazy(
  () =>
    import(
      /* webpackChunkName: "folders" */ '../../folders/page/routes/FoldersRoutes'
    ),
);

const DomainApp = lazy(
  () => import(/* webpackChunkName: "domain" */ '../../domain/DomainApp'),
);

const Dashboard = lazy(
  () => import(/* webpackChunkName: "dashboard" */ '../../dashboard/Dashboard'),
);

const ConfirmEmailUpdate = lazy(
  () =>
    import(
      /* webpackChunkName: "confirmEmailUpdate" */ '../../confirm-email-update/ConfirmEmailUpdate'
    ),
);

const Contact = lazy(
  () => import(/* webpackChunkName: "contact" */ '../../contact/Contact'),
);

const DecisionUpload = lazy(
  () =>
    import(
      /* webpackChunkName: "DecisionUpload" */ '../../upload/decision-upload/page/DecisionUploadApp'
    ),
);

const DocumentApp = lazy(
  () =>
    import(/* webpackChunkName: "documents" */ '../../document/DocumentApp'),
);

export const DoctrineRoutes: FC = ({ children }) => {
  const {
    loggedIn,
    currentUser: { plan, payingOptions },
  } = useUser();

  const allowDocumentAnalyzer = canSeeDocumentAnalyzer(
    plan,
    payingOptions,
    loggedIn,
  );
  const allowDocumentAnalyzerComparison = canSeeDocumentsComparison(
    plan,
    payingOptions,
    loggedIn,
  );
  return (
    <>
      <Suspense fallback={<PageLayoutFallback />}>
        <Switch>
          <Route exact path="/search" component={SearchMain} />
          <Route
            exact
            path="/choix_mdp/:user_id/:code"
            component={PageLayout}
          />
          <Route
            exact
            path="/alertes"
            component={() => (
              <PageLayout>
                <LightHelmet
                  title="Alertes | Doctrine"
                  pageName="SearchWatch"
                />
                <Watchers />
              </PageLayout>
            )}
          />
          <Route
            exact
            path="/alertes/:key"
            render={(routerParams) => {
              const watcherKey = routerParams.match.params.key;
              return (
                <>
                  <LightHelmet
                    title="Alertes | Doctrine"
                    pageName="SearchWatch"
                  />
                  <WatchersDetails watcherKey={watcherKey} />
                </>
              );
            }}
          />
          {allowDocumentAnalyzer && (
            <Route
              exact
              path="/document-analyzer"
              component={DocumentAnalyzerApp}
            />
          )}
          {allowDocumentAnalyzer && (
            <Route
              exact
              path="/document-analyzer/analyse"
              component={DocumentAnalyzerAnalysisApp}
            />
          )}
          {allowDocumentAnalyzerComparison && (
            <Route
              exact
              path="/document-analyzer/comparaison"
              component={DocumentAnalyzerComparisonApp}
            />
          )}
          <Redirect
            path="/document-analyzer/comparison"
            to="/document-analyzer/comparaison"
          />
          <Route
            exact
            path={['/historique_recherches', '/historique_pages']}
            component={HistoryApp}
          />
          <Route exact path="/dossiers*" component={FoldersRoutes} />
          <Route
            exact
            path={ACCOUNT_NAV_ITEMS.legal_domains.path}
            component={DomainApp}
          />
          <Route
            exact
            path={['/', '/connexion', '/actu/*', '/actu']}
            component={Dashboard}
          />
          <Route exact path={UPDATE_EMAIL_URL} component={ConfirmEmailUpdate} />
          <Route exact path="/contact" component={Contact} />
          <Route
            exact
            path={['/j/:all', '/j/:all/:all']}
            component={DecisionUpload}
          />
          {allowDocumentAnalyzer && (
            <Route exact path="/documents" component={DocumentApp} />
          )}
          {/* Handle default MPA route during SPA migration - to be deleted */}
          <Route render={() => children} />
        </Switch>
      </Suspense>
    </>
  );
};
